import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import * as React from 'react';
import _ from 'lodash';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { WATCH_LIST_QUERY_PARAMS } from 'pages/TrackAndTrace/constants';
import { WatchListFilterCounts } from 'services/clients/watchList';
import { brandColors } from 'brandColors';
import { STATUS_FILTERS, StatusFilter } from './types';
import { FilterOption } from './StatusFilterOption';
import { milestoneFiltersOn } from '../filters/utils';

interface Props {
  filterCounts?: WatchListFilterCounts;
}

const isFilterOn = (
  searchParams: Record<string, string[]>,
  filterSearchParams: Record<string, string[]>
) => {
  return Object.entries(filterSearchParams).every(([key, values]) => {
    const searchParamsValues = _.get(searchParams, key, []);
    const valuesFound = values.every((v: string) => searchParamsValues.includes(v as never));
    if (!valuesFound) {
      return false;
    }
    return true;
  });
};

/**
 * Renders a list of checkboxes for filtering by container status
 */
export const StatusSelector: React.FC<Props> = ({ filterCounts }) => {
  const { searchParams, setSearchParams } = useCustomSearchParams(WATCH_LIST_QUERY_PARAMS);

  const handleOnStatusFilterToggle = (status: StatusFilter, isActive: boolean) => {
    const newSearchParams: Record<string, string[] | number> = { page: 1 };
    Object.entries(status.searchParams).forEach(([key, values]) => {
      if (isActive) {
        // if the filter is active we remove all the values
        const currentValues = searchParams[key];
        const newValues = _.difference(currentValues, values);
        newSearchParams[key] = newValues;
      } else {
        // if the filter is not active we add all the values
        const currentValues = searchParams[key];
        const newValues = [...new Set([...currentValues, ...values])];
        newSearchParams[key] = newValues;
      }
    });
    setSearchParams(newSearchParams);
  };

  const clearStatusFilters = () => {
    const newSearchParams: Record<string, string[] | number> = {
      page: 1,
    };
    STATUS_FILTERS.forEach((statusFilter) => {
      Object.keys(statusFilter.searchParams).forEach((key) => {
        newSearchParams[key] = [];
      });
    });
    setSearchParams(newSearchParams);
  };

  const milestoneFiltersChecked =
    milestoneFiltersOn(searchParams) || searchParams.containerNumbers.length > 0;
  const allCount = milestoneFiltersChecked
    ? _.sum(
        _.map(_.keys(filterCounts), (k) =>
          k.startsWith('current') && filterCounts
            ? filterCounts[k as keyof WatchListFilterCounts]
            : 0
        )
      )
    : filterCounts?.totalWatched || 0;

  const hasFiltersApplied = STATUS_FILTERS.some((f) => isFilterOn(searchParams, f.searchParams));

  return (
    <FormGroup className="flex flex-row">
      <FormControlLabel
        key="all"
        control={
          <Checkbox
            onChange={clearStatusFilters}
            checked={!hasFiltersApplied}
            size="small"
            className="pr-1"
            indeterminate={hasFiltersApplied}
            sx={{
              '&.MuiCheckbox-indeterminate': {
                color: brandColors.colors.gray[300],
              },
            }}
          />
        }
        label={`All (${allCount})`}
        className="pr-4"
      />
      {STATUS_FILTERS.map((statusFilter) => {
        const active = isFilterOn(searchParams, statusFilter.searchParams);
        return (
          <FilterOption
            key={statusFilter.type}
            statusFilter={statusFilter}
            onStatusFilterToggle={() => handleOnStatusFilterToggle(statusFilter, active)}
            active={active}
            milestoneFiltersChecked={milestoneFiltersChecked}
            currentFilterCount={_.get(filterCounts, `current${statusFilter.filterCountKey}`, 0)}
            totalFilterCount={_.get(filterCounts, `total${statusFilter.filterCountKey}`, 0)}
          />
        );
      })}
    </FormGroup>
  );
};
StatusSelector.displayName = 'TrackAndTrace.Controls.StatusSelector';
